import React from "react";

import Script from "next/script";
import { IS_PROD } from "lib/config";
import { reportError } from "lib/errors/errors";

/*
 ** gtag is a global variable we wrap around the GTM by Google Tag Manager.
 ** Use the pztag version below to send events to the GTM.
 */

const GTM_PREFIX = "PZ_" as const;
const ONBOARDING_COMPLETED_EVENT_NAME = "onboarding_completed";
const NEW_SUBSCRIPTION_EVENT_DESTINATION = "AW-11179879045/G2yfCImNucEZEIXV_dIp";

/**
 * Sends custom events to Google Tag Manager with PZ_ prefix
 * @param eventName - The name of the event to track
 * @param eventParams - Optional parameters to send with the event
 */
export const pztag = (
  eventName: string,
  eventParams?: Gtag.ControlParams | Gtag.EventParams | Gtag.CustomParams
) => {
  if (typeof gtag !== "undefined") {
    gtag("event", GTM_PREFIX + eventName, eventParams);
  }
};

/**
 * Sends the onboarding completed event to GTM
 */
export const onboardingCompletedGtag = () => {
  pztag(ONBOARDING_COMPLETED_EVENT_NAME);
};

/**
 * Google Tag Manager script component
 * Handles GTM initialization.
 *
 * @param id - The Google Tag Manager ID
 * @param env - The Google Tag Manager environment parameters
 */
export const GtmScript = ({ id, env }: { id?: string; env?: string }) => {
  if (!id) return null;

  return (
    <>
      {/* Main GTM script */}
      <Script
        id="gtm-script"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
          (function(w,d,s,l,i){
            try {
              w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});
              var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';
              j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl+'${env ?? ""}';
              f.parentNode.insertBefore(j,f);
            } catch(e) {
              console.warn('GTM initialization failed:', e);
            }
          })(window,document,'script','dataLayer','${id}');
        `,
        }}
      />
      {/* NoScript fallback for GTM */}
      <noscript>
        <iframe
          src={`https://www.googletagmanager.com/ns.html?id=${id}${env ?? ""}`}
          height="0"
          width="0"
          style={{ display: "none", visibility: "hidden" }}
          title="Google Tag Manager"
        ></iframe>
      </noscript>
    </>
  );
};

/**
 * Initializes gtag.js with the provided ID
 *
 * @param id - The Google Tag Manager ID
 */
export const GtagScript = ({ id }: { id?: string }) => {
  if (!id) return null;

  return (
    <Script
      id="gtag-init"
      strategy="afterInteractive"
      dangerouslySetInnerHTML={{
        __html: `
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', '${id}');
        `,
      }}
    />
  );
};

/**
 * Sends a Google Ads conversion event for a new subscription.
 *
 * This function triggers a "conversion" event using gtag, sending the subscription price
 * to the configured Google Ads destination. It only runs in production and if the price is a valid number.
 *
 * @param planPrice - The price of the new subscription (as a string, will be converted to number)
 */
export const sendNewSubscriptionEvent = (planPrice: string) => {
  const price = Number(planPrice);
  if (IS_PROD && !isNaN(price) && typeof gtag !== "undefined") {
    try {
      // We are not using the pztag here because it adds a prefix to the event name.
      // This prefix might interfere with existing Google Ads automations or reporting.
      gtag("event", "conversion", {
        send_to: NEW_SUBSCRIPTION_EVENT_DESTINATION,
        value: planPrice,
        currency: "USD",
        event_callback: () => undefined,
      });
    } catch (error: any) {
      reportError(error);
    }
  }
};
